import { useOptionalDependency } from 'ditox-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FIREBASE_MANAGER_TOKEN } from '@/container';
import useUpdateDeviceToken from '@/hooks/fcm/use-update-device-token';
import { useAuthStore } from '@/stores/auth';
import { setNotificationPermissionRequested, useDeviceStore } from '@/stores/device';
import { makePrompt } from '@/stores/prompter';
import { timeoutPromise } from '@/utils/promise';

export default function useNotificationPermission() {
  const { t } = useTranslation();
  const isSignedIn = useAuthStore((s) => Boolean(s.auth?.UserID));
  const firebaseManager = useOptionalDependency(FIREBASE_MANAGER_TOKEN);
  const { mutate: updateDeviceToken } = useUpdateDeviceToken();
  const { fcmToken, isNotificationPermissionRequested } = useDeviceStore();

  useEffect(() => {
    if (firebaseManager) {
      (async () => {
        const shouldPrompt = await firebaseManager.shouldRequestPermission();

        if (shouldPrompt && !isNotificationPermissionRequested) {
          const resolution = await makePrompt({
            key: 'request-notification-permission',
            title: t('permission-request.notifications.title'),
            content: t('permission-request.notifications.content'),
          });

          setNotificationPermissionRequested(true);
          if (resolution === true || resolution === 'confirm') {
            await timeoutPromise(
              async () => firebaseManager.initialize(),
              3_000,
              () => void 0,
            );
          }
        }
      })();
    }
  }, [firebaseManager, isNotificationPermissionRequested]);

  useEffect(() => {
    if (firebaseManager) {
      (async () => {
        const canSendNotification = await firebaseManager.shouldRequestPermission('granted');
        if (isSignedIn && canSendNotification) {
          setNotificationPermissionRequested(true);
          await firebaseManager.initialize().catch(console.warn);
        }
      })();
    }
  }, [isSignedIn, firebaseManager]);

  useEffect(() => {
    if (fcmToken && isSignedIn) {
      updateDeviceToken(fcmToken);
    }
  }, [fcmToken, isSignedIn]);
}
